"use client";

import { userStore } from "@/store/user";
import { Popover, Upload, UploadProps } from "antd";
import type { TooltipPlacement } from "antd/es/tooltip";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import FromAssetsIcon from "./assets/from-assets.svg";
import FromLocalIcon from "./assets/from-local.svg";

export interface AssetsSelectorProps {
  uploadProps: UploadProps;
  onSelectFromAssets: () => void;
  disabled?: boolean;
  children?: React.ReactNode;
  placement?: TooltipPlacement;
}

export const AssetsSelector = observer(function AssetsSelector(
  props: AssetsSelectorProps,
) {
  const {
    uploadProps,
    onSelectFromAssets,
    disabled = false,
    children,
    placement = "bottomLeft",
  } = props;
  const [open, setOpen] = useState(false);
  const router = useRouter();

  const handleSelectFromAssets = () => {
    setOpen(false);

    // Check if user is logged in before opening assets modal
    if (!userStore.isLogin) {
      router.push("/login");
      return;
    }

    onSelectFromAssets();
  };

  const content = (
    <div className="w-full flex flex-col">
      <Upload
        {...uploadProps}
        className="w-full"
        disabled={disabled}
        showUploadList={false}
      >
        <div
          className={twMerge(
            "w-full px-3 py-2.5 text-sm text-text-secondary rounded-lg cursor-pointer transition-colors flex items-center gap-2",
            disabled
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-[#31313B] hover:text-text-primary",
          )}
          onClick={() => setOpen(false)}
        >
          <FromLocalIcon className="w-5 h-5" />
          Upload from Local
        </div>
      </Upload>
      <div
        className={twMerge(
          "w-full px-3 py-2.5 text-sm text-text-secondary rounded-lg cursor-pointer transition-colors flex items-center gap-2",
          disabled
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-[#31313B] hover:text-text-primary",
        )}
        onClick={disabled ? undefined : handleSelectFromAssets}
      >
        <FromAssetsIcon className="w-5 h-5" />
        Select from Assets
      </div>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger={disabled ? [] : ["hover"]}
      placement={placement}
      arrow={false}
      classNames={{
        root: "!pt-1",
        body: "p-2! bg-background-elevated! rounded-[12px]!",
      }}
      content={content}
    >
      {children}
    </Popover>
  );
});
